import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useAuthStore } from '../../features/auth/store/useAuthStore.js';
import { useNotificationStore } from '../../features/notifications/store/useNotificationStore.js';
import {
  NavbarBrand,
  NavLinks,
  NotificationBell,
  UserDropdown,
  AuthButtons,
  MobileNavDrawer,
  NavTab,
} from './navbar/index.js';
import { PWAInstallButton } from './PWAInstallButton.js';

interface NavbarProps {
  currentTab: NavTab;
  onSelectTab: (tab: NavTab) => void;
  onOpenLogin: () => void;
  onOpenRegister: () => void;
  onOpenProfile?: () => void;
  onOpenShowcase?: (type: 'penulis' | 'penerbit', identifier: string) => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  currentTab,
  onSelectTab,
  onOpenLogin,
  onOpenRegister,
  onOpenProfile,
  onOpenShowcase,
}) => {
  const { user, logout } = useAuthStore();
  const { unreadCount, toggleOpen, showToast } = useNotificationStore();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const handleLogout = () => {
    logout();
    showToast('Berhasil Keluar', 'Anda telah keluar dari akun UIBN.', 'info');
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-2xs">
      <div className="max-w-7xl 2xl:max-w-[1600px] 3xl:max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12">
        <div className="flex items-center justify-between h-16 2xl:h-18">
          {/* Brand Logo & Desktop Navigation */}
          <div className="flex items-center gap-6">
            <NavbarBrand onSelectTab={onSelectTab} />
            <NavLinks
              currentTab={currentTab}
              user={user}
              onSelectTab={onSelectTab}
            />
          </div>

          {/* Right Actions (Notification + User Menu / Auth) */}
          <div className="flex items-center gap-2.5">
            <PWAInstallButton className="hidden md:flex" />
            
            {/* Notification Bell */}
            {user && (
              <NotificationBell
                unreadCount={unreadCount}
                onToggle={toggleOpen}
              />
            )}

            {user ? (
              /* User dropdown profile */
              <UserDropdown
                user={user}
                isOpen={isUserMenuOpen}
                onToggleOpen={() => setIsUserMenuOpen(!isUserMenuOpen)}
                onClose={() => setIsUserMenuOpen(false)}
                onSelectTab={onSelectTab}
                onOpenProfile={onOpenProfile}
                onOpenShowcase={onOpenShowcase}
                onLogout={handleLogout}
              />
            ) : (
              /* Auth & Register buttons */
              <AuthButtons
                onOpenLogin={onOpenLogin}
                onOpenRegister={onOpenRegister}
              />
            )}

            {/* Mobile menu toggle */}
            <button
              type="button"
              onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
              className="md:hidden p-2 rounded-xl text-slate-500 hover:bg-slate-100 cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Toggle Navigation Menu"
            >
              {isMobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      <MobileNavDrawer
        isOpen={isMobileNavOpen}
        currentTab={currentTab}
        user={user}
        onSelectTab={onSelectTab}
        onCloseMobileNav={() => setIsMobileNavOpen(false)}
        onOpenProfile={onOpenProfile}
      />
    </header>
  );
};
